<template>
  <div class="rate flex" :class="{ 'is-disabled': disabled }">
    <div
      v-for="(item, index) in Number(max)"
      :key="index"
      class="rate-item"
      :class="rateClass(item)"
      @mousemove="previewRate(item, $event)"
      @mouseleave="resetNewValue"
      @click.prevent="confirmValue(item)"
    >
      <ak-icon :type="iconClass(item)" :size="size" />
    </div>
    <div
      v-if="showText || showScore || customText"
      class="rate-text ml-tiny"
      :class="size"
    >
      <span>{{ showMe }}</span>
      <span v-if="customText && !showText">{{ customText }}</span>
    </div>
  </div>
</template>

<script>
import {
  defineComponent,
  computed,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
} from 'vue'

import { rateProps, validationProps } from '../../mixins/formPropsMixin'

import Icon from '../icon/Icon.vue'
import validationMixin from '../../mixins/validationMixin'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-rate />
   */
  name: 'AkRate',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [Icon.name]: Icon,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    ...rateProps,
    ...validationProps,
  },

  emits: ['update:modelValue'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @param {Function} options.attrs - The attributes for the component.
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit, attrs }) {
    const hoverValue = ref(0)

    const computedValue = computed({
      get() {
        return props.modelValue
      },
      set(value) {
        emit('update:modelValue', value)
      },
    })

    const input = ref({
      validationMessage:
        props.validationMessage ||
        'Please choose a rating if you want to proceed.',
      value: computedValue.value,
      checkValidity: () => {
        return computedValue.value > 0
      },
    })

    /**
     * @returns {String}
     * @description Returns the label text for the rate component
     */
    const showMe = computed(() => {
      let result = ''
      if (props.showScore) {
        result = props.disabled ? props.modelValue : computedValue.value
      } else if (props.showText) {
        result = props.texts[Math.ceil(computedValue.value) - 1]
      }
      return result
    })

    /**
     * @param {Number} index
     * @returns {String}
     * @description Returns the icon class for the component
     */
    const iconClass = (index) => {
      return Math.floor(index) <= Math.floor(computedValue.value)
        ? 'star'
        : 'star-outline'
    }

    /**
     * @description Resets the hover value.
     */
    const resetNewValue = () => {
      if (props.disabled) return
      hoverValue.value = 0
    }

    /**
     * @param {Number} index
     * @param {Object} event
     * @description Sets the hover value.
     */
    const previewRate = (index, event) => {
      if (props.disabled) return
      hoverValue.value = index
      event.stopPropagation()
    }

    /**
     * @param {Number} index
     * @description Sets the value of the component.
     */
    const confirmValue = (index) => {
      if (props.disabled) return
      if (computedValue.value !== index) {
        computedValue.value = index
      } else {
        computedValue.value = 0
      }
    }

    /**
     * @param {Number} index
     * @returns {String}
     * @description Returns the classes for the rating star
     */
    const rateClass = (index) => {
      let output = ''
      const currentValue =
        hoverValue.value !== 0 ? hoverValue.value : computedValue.value
      if (index <= currentValue) {
        output = 'set-on color-stars'
      }
      return output
    }

    /**
     * @type {Object}
     * @description The validation mixin return value, this is an object
     * containing all of the required functionality to handle the
     * validation of the input
     */
    const validation = validationMixin({
      type: 'rate',
      emit,
      props,
      attrs,
      input,
    })

    onMounted(() => validation.validate())
    onBeforeUnmount(() => validation.beforeUnmount())

    watch(computedValue, () => {
      validation.validate()
    })

    return {
      computedValue,
      hoverValue,
      showMe,
      iconClass,
      resetNewValue,
      previewRate,
      confirmValue,
      rateClass,
      ...validation,
    }
  },
})
</script>
